import { useCallback, useEffect, useRef, useState } from 'react';

import { useValidateDraft } from '@/hooks/useValidateDraft';

interface UseDraftValidationOptions {
  endpoint: string;
  debounceMs?: number;
}

// Background, debounced authoring-validation. Advisory only — the recommendation
// is a warning, never blocks save. Consumers call scheduleValidate on each form
// edit; the hook debounces, fires one validate after the idle window, and
// exposes the latest recommendation for an unobtrusive banner.
export const useDraftValidation = <TBody>({
  endpoint,
  debounceMs = 1000,
}: UseDraftValidationOptions) => {
  const [recommendation, setRecommendation] = useState('');
  const { validate } = useValidateDraft<TBody>({ endpoint });

  // Monotonic counter so stale in-flight responses can't overwrite a newer result
  // or a reset. Reset bumps this too.
  const requestIdRef = useRef(0);
  // Last recommendation returned for this session — forwarded on the next validate
  // so the LLM can consider what it already suggested and avoid repeating itself.
  const prevRecommendationRef = useRef('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPending = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const scheduleValidate = useCallback(
    (body: TBody) => {
      if (!endpoint) {
        return;
      }
      cancelPending();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const id = ++requestIdRef.current;
        void validate(body, prevRecommendationRef.current || undefined).then((outcome) => {
          if (id !== requestIdRef.current) {
            return;
          }
          if (outcome.kind === 'degraded') {
            return;
          }
          const rec = outcome.result.recommendation || '';
          setRecommendation(rec);
          if (rec) {
            prevRecommendationRef.current = rec;
          }
        });
      }, debounceMs);
    },
    [cancelPending, debounceMs, endpoint, validate]
  );

  const reset = useCallback(() => {
    cancelPending();
    requestIdRef.current++;
    setRecommendation('');
    prevRecommendationRef.current = '';
  }, [cancelPending]);

  useEffect(() => cancelPending, [cancelPending]);

  return { recommendation, scheduleValidate, reset };
};
